import React, { useState, useMemo } from "react";
import { CallType } from "../../models/Call.model";
import { DateInput } from "./DateInput";
import {
  Phone,
  Clock,
  Calendar,
  ChevronDown,
  Filter,
  X,
  ChevronUp,
  MessageSquare,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

import { format, isValid, parseISO } from "date-fns";

interface CallHistoryProps {
  leadId: string;
}

export const CallLogs: React.FC<CallHistoryProps> = ({ leadId }) => {
  const callLogs = useSelector(
    (state: RootState) => state.telecallerLead.callLogs
  );
  const loading = useSelector(
    (state: RootState) =>
      state.telecallerLead.loadingAndErrorState.fetchingTelecallerLead
  );

  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  const [selectedDate, setSelectedDate] = useState<string>("");
  const [showDateFilter, setShowDateFilter] = useState<boolean>(false);

  const filteredCallLogs = useMemo(() => {
    if (!callLogs) return [];

    const filtered = callLogs.filter((log) => {
      return (
        log.leadId === leadId &&
        (!selectedDate || log.callDate === selectedDate)
      );
    });

    return filtered.sort((a, b) => {
      const dateTimeA = new Date(
        `${a.callDate.split("-").reverse().join("-")} ${a.startTime}`
      );
      const dateTimeB = new Date(
        `${b.callDate.split("-").reverse().join("-")} ${b.startTime}`
      );
      return dateTimeB.getTime() - dateTimeA.getTime();
    });
  }, [callLogs, selectedDate, leadId]);

  const toggleAccordion = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const clearDateFilter = () => {
    setSelectedDate("");
    setShowDateFilter(false);
  };

  const handleDateChange = (date: string | Date) => {
    let formattedDate = "";

    if (typeof date === "string") {
      const parsed = parseISO(date);
      formattedDate = isValid(parsed) ? format(parsed, "dd-MM-yyyy") : date;
    } else if (date instanceof Date && isValid(date)) {
      formattedDate = format(date, "dd-MM-yyyy");
    }

    setSelectedDate(formattedDate);
    setShowDateFilter(false);
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="p-4 rounded-2xl sm:p-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2 sm:gap-3">
            Call History
          </h2>
          <button
            onClick={() => setShowDateFilter(!showDateFilter)}
            className={`p-2 rounded-lg border transition-all duration-200 ${
              showDateFilter || selectedDate
                ? "bg-blue-100 border-blue-300 text-blue-700"
                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
            title="Filter by date"
          >
            <Filter className="h-4 w-4" />
          </button>
        </div>

        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            showDateFilter ? "max-h-32 opacity-100 mb-4" : "max-h-0 opacity-0"
          }`}
        >
          <div className="bg-white p-4 border-gray-200 shadow-sm">
            <div className="flex gap-3 items-center">
              <div className="flex-1">
                <DateInput
                  selectedDate={selectedDate}
                  onDateChange={handleDateChange}
                />
              </div>
              {selectedDate && (
                <button
                  onClick={clearDateFilter}
                  className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                  title="Clear filter"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="p-3 flex-1 overflow-y-auto max-h-screen">
        <div className="space-y-2">
          {filteredCallLogs.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl flex items-center justify-center">
                <Phone className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                No call logs found
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                {selectedDate
                  ? `No calls found for selected date`
                  : "No call history available"}
              </p>
              {selectedDate && (
                <button
                  onClick={clearDateFilter}
                  className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
                >
                  Show All
                </button>
              )}
            </div>
          ) : (
            filteredCallLogs.map((log, i) => {
              const isNotPickedUp = log?.wasPickedUp === false;
              return (
                <div
                  key={i}
                  className="group bg-white rounded-lg border border-gray-200/60 hover:border-gray-300/60 hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  <div className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="relative flex-shrink-0">
                        <div className="w-8 h-8 bg-gradient-to-br rounded-lg flex items-center justify-center text-white font-medium text-xs">
                          <Phone className="w-4 h-4 text-blue-500" />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {log?.number}
                          </h3>
                        </div>

                        <div className="space-y-0.5 flex gap-2 sm:flex-row">
                          {log?.callDate && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <Calendar className="h-3 w-3" />
                              <span className="truncate">{log.callDate}</span>
                            </div>
                          )}
                          {log?.duration && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <Clock className="h-3 w-3" />
                              <span>
                                {isNotPickedUp ? "Not picked up" : log.duration}{" "}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => toggleAccordion(i)}
                        className="p-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-md transition-all duration-200 flex-shrink-0"
                      >
                        {expandedIndex === i ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )}
                      </button>
                    </div>

                    {expandedIndex === i && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 bg-yellow-100 rounded-md flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="h-3 w-3 text-yellow-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-1">Remark</p>
                            <p className="text-xs text-gray-700">
                              {log?.remark || "No remark provided"}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bottom indicator */}
                  <div
                    className={`h-0.5 ${
                      isNotPickedUp
                        ? "bg-gradient-to-r from-red-400 to-red-500"
                        : log?.type === CallType.INCOMING_TYPE
                        ? "bg-gradient-to-r from-green-400 to-green-500"
                        : log?.type === CallType.OUTGOING_TYPE
                        ? "bg-gradient-to-r from-blue-400 to-blue-500"
                        : "bg-gradient-to-r from-red-400 to-red-500"
                    }`}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
